import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, List, Rows3 } from "lucide-react";
import { useMemo, useState } from "react";
import { ErrorState, LoadingState, PageHeading, StatusBadge } from "@/components/app/AppUI";
import { eventRepository, taskRepository } from "@/data/repositories";

export const Route = createFileRoute("/_authenticated/app/calendario")({ component: CalendarPage });

function CalendarPage() {
  const events = useQuery({ queryKey: ["events"], queryFn: () => eventRepository.list() });
  const tasks = useQuery({ queryKey: ["tasks"], queryFn: () => taskRepository.list() });
  const [cursor, setCursor] = useState(() => new Date());
  const [mode, setMode] = useState<"month" | "list">("month");
  const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const gridStart = new Date(monthStart);
  gridStart.setDate(gridStart.getDate() - ((gridStart.getDay() + 6) % 7));
  const days = Array.from({ length: 42 }, (_, index) => {
    const day = new Date(gridStart);
    day.setDate(day.getDate() + index);
    return day;
  });
  const eventMap = useMemo(
    () =>
      new Map(
        (events.data ?? [])
          .filter((event) => event.starts_at)
          .map((event) => [new Date(event.starts_at as string).toISOString().slice(0, 10), event]),
      ),
    [events.data],
  );
  if (events.isLoading || tasks.isLoading) return <LoadingState />;
  if (events.error || tasks.error)
    return <ErrorState message={events.error?.message ?? tasks.error?.message ?? "Error"} />;
  return (
    <div className="space-y-7">
      <PageHeading
        eyebrow="Agenda"
        title="Calendario"
        description="Eventos y tareas con fecha, en una vista útil también desde móvil."
        action={
          <div className="flex gap-2">
            <button
              className={mode === "month" ? "btn-primary" : "btn-quiet"}
              onClick={() => setMode("month")}
            >
              <Rows3 className="size-4" /> Mes
            </button>
            <button
              className={mode === "list" ? "btn-primary" : "btn-quiet"}
              onClick={() => setMode("list")}
            >
              <List className="size-4" /> Lista
            </button>
          </div>
        }
      />
      {mode === "month" ? (
        <section className="panel overflow-hidden">
          <div className="panel-header">
            <button
              className="btn-quiet !px-3"
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            >
              <ChevronLeft className="size-4" />
            </button>
            <h2 className="font-display text-2xl capitalize">
              {new Intl.DateTimeFormat("es-ES", { month: "long", year: "numeric" }).format(cursor)}
            </h2>
            <button
              className="btn-quiet !px-3"
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            >
              <ChevronRight className="size-4" />
            </button>
          </div>
          <div className="hidden grid-cols-7 border-b border-border bg-secondary md:grid">
            {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((day) => (
              <div key={day} className="eyebrow p-3 text-center">
                {day}
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-7">
            {days.map((day) => {
              const key = day.toISOString().slice(0, 10);
              const event = eventMap.get(key);
              const outside = day.getMonth() !== cursor.getMonth();
              return (
                <div
                  key={key}
                  className={`min-h-24 border-b border-r border-border p-2 ${outside ? "bg-secondary/40 text-muted-foreground" : "bg-white"}`}
                >
                  <time
                    className={`font-mono text-xs ${key === new Date().toISOString().slice(0, 10) ? "inline-grid size-6 place-items-center rounded-full bg-primary text-white" : ""}`}
                  >
                    {day.getDate()}
                  </time>
                  {event && (
                    <Link
                      to="/app/eventos/$id"
                      params={{ id: event.id }}
                      className="mt-2 block border-l-2 border-primary bg-primary/7 p-2 text-sm"
                    >
                      <span className="block truncate font-semibold">{event.title}</span>
                      <span className="text-xs text-muted-foreground">
                        {event.starts_at
                          ? new Intl.DateTimeFormat("es-ES", {
                              hour: "2-digit",
                              minute: "2-digit",
                            }).format(new Date(event.starts_at))
                          : ""}
                      </span>
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ) : (
        <section className="panel">
          {(events.data ?? []).map((event) => (
            <Link
              key={event.id}
              to="/app/eventos/$id"
              params={{ id: event.id }}
              className="data-row grid-cols-[6rem_1fr_auto]"
            >
              <time className="font-mono text-xs text-primary">
                {event.starts_at
                  ? new Intl.DateTimeFormat("es-ES", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    }).format(new Date(event.starts_at))
                  : "Sin fecha"}
              </time>
              <div>
                <p className="font-semibold">{event.title}</p>
                <p className="text-sm text-muted-foreground">{event.venue ?? "Lugar pendiente"}</p>
              </div>
              <StatusBadge status={event.status} />
            </Link>
          ))}
        </section>
      )}
    </div>
  );
}
